/**
 * A person's notes (migration 1058; PRD-tasks-asks-and-my-work §4.5, FR-6).
 *
 * What a person wrote down for themself: text, optionally on a record, optionally
 * with a reminder. Read by its owner only; written only through the five acts
 * below. A note whose reminder has come is an ask in what needs them (engine row
 * 1q) with Done on it; a note with no date never is. Notes are the one thing a
 * person finishes by hand, because a note is its own subject.
 */
import { getTypedSupabaseClient } from './typedSupabaseClient';

export type NoteSubject = 'item' | 'cycle' | 'track' | 'session' | 'decision' | 'commitment';

export interface PersonalNote {
  id: string;
  body: string;
  subject_type: NoteSubject | null;
  subject_id: string | null;
  remind_at: string | null;
  done_at: string | null;
  created_at: string;
  updated_at: string;
}

type Result<T = void> = { ok: true; data: T } | { ok: false; error: string };

const db = () => getTypedSupabaseClient();

/** My notes in this workspace: open ones, and those done in the last thirty days. */
export async function listMyNotes(userId: string, workspaceId: string): Promise<PersonalNote[]> {
  const since = new Date(Date.now() - 30 * 86_400_000).toISOString();
  const { data, error } = await db()
    .from('personal_notes')
    .select('id, body, subject_type, subject_id, remind_at, done_at, created_at, updated_at')
    .eq('user_id', userId)
    .eq('workspace_id', workspaceId)
    .or(`done_at.is.null,done_at.gte.${since}`)
    .order('created_at', { ascending: false });
  if (error) throw new Error(error.message);
  return (data ?? []) as PersonalNote[];
}

export async function createNote(input: {
  workspaceId: string;
  body: string;
  subjectType?: NoteSubject | null;
  subjectId?: string | null;
  remindAt?: string | null;
}): Promise<Result<string>> {
  const { data, error } = await db().rpc('create_note', {
    p_workspace_id: input.workspaceId,
    p_body: input.body,
    p_subject_type: input.subjectType ?? undefined,
    p_subject_id: input.subjectId ?? undefined,
    p_remind_at: input.remindAt ?? undefined,
  });
  return error ? { ok: false, error: error.message } : { ok: true, data: data as string };
}

export async function updateNote(noteId: string, body: string, remindAt: string | null): Promise<Result> {
  const { error } = await db().rpc('update_note', { p_note_id: noteId, p_body: body, p_remind_at: remindAt ?? undefined });
  return error ? { ok: false, error: error.message } : { ok: true, data: undefined };
}

export async function completeNote(noteId: string): Promise<Result> {
  const { error } = await db().rpc('complete_note', { p_note_id: noteId });
  return error ? { ok: false, error: error.message } : { ok: true, data: undefined };
}

export async function reopenNote(noteId: string): Promise<Result> {
  const { error } = await db().rpc('reopen_note', { p_note_id: noteId });
  return error ? { ok: false, error: error.message } : { ok: true, data: undefined };
}

export async function deleteNote(noteId: string): Promise<Result> {
  const { error } = await db().rpc('delete_note', { p_note_id: noteId });
  return error ? { ok: false, error: error.message } : { ok: true, data: undefined };
}

/** Said when a note changes, so My work and Home reload. */
export const NOTES_CHANGED_EVENT = 'pp:notes-changed';
export function announceNotesChanged(): void {
  if (typeof window !== 'undefined') window.dispatchEvent(new Event(NOTES_CHANGED_EVENT));
}
